// src/admin/Analytics.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getVisitorStats } from '../utils/trackVisitor';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import {
  TrendingUp, Users, ShoppingBag, DollarSign, Eye, Package,
  Star, Activity, Calendar, ArrowUp, ArrowDown
} from 'lucide-react';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [visitorStats, setVisitorStats] = useState(null);

  // ================= FETCH DATA =================
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [ordersSnap, productsSnap, stats] = await Promise.all([
          getDocs(collection(db, 'orders')),
          getDocs(collection(db, 'products')),
          getVisitorStats(),
        ]);

        setOrders(ordersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setProducts(productsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setVisitorStats(stats);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();

    // Refresh every 30 seconds
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-pink-400 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading analytics...</p>
      </div>
    );
  }

  // ================= STATS CALCULATIONS =================
  const totalOrders = orders.length;
  const totalRevenue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
  const avgOrderValue = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;
  const completedOrders = orders.filter(o => o.status === 'completed').length;
  const processingOrders = orders.filter(o => !o.status || o.status === 'processing').length;

  // ================= REVENUE TREND (Last 7 days) =================
  const getRevenueTrend = () => {
    const days = 7;
    const data = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-PK', { day: 'numeric', month: 'short' });
      const dayKey = date.toISOString().split('T')[0];

      const dayOrders = orders.filter(o => {
        if (!o.createdAt) return false;
        const orderDate = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt.seconds * 1000);
        return orderDate.toISOString().split('T')[0] === dayKey;
      });

      const revenue = dayOrders.reduce((sum, o) => sum + (o.total || 0), 0);

      data.push({
        date: dateStr,
        revenue,
        orders: dayOrders.length,
      });
    }

    return data;
  };

  // ================= TOP PRODUCTS =================
  const getTopProducts = () => {
    const productSales = {};

    orders.forEach(order => {
      (order.items || []).forEach(item => {
        const key = item.id || item.title;
        if (!productSales[key]) {
          productSales[key] = {
            title: item.title,
            quantity: 0,
            revenue: 0,
          };
        }
        productSales[key].quantity += item.quantity || 0;
        productSales[key].revenue += (item.price * item.quantity) || 0;
      });
    });

    return Object.values(productSales)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);
  };

  // ================= ORDER STATUS PIE =================
  const getOrderStatusData = () => {
    const statuses = {
      processing: 0,
      completed: 0,
      cancelled: 0,
      shipped: 0,
      'on-hold': 0,
    };

    orders.forEach(o => {
      const s = o.status || 'processing';
      if (statuses[s] !== undefined) {
        statuses[s]++;
      }
    });

    return [
      { name: 'Processing', value: statuses.processing, color: '#F5AEC0' },
      { name: 'Completed', value: statuses.completed, color: '#DC829A' },
      { name: 'Shipped', value: statuses.shipped, color: '#9B3F5B' },
      { name: 'Cancelled', value: statuses.cancelled, color: '#EF4444' },
      { name: 'On Hold', value: statuses['on-hold'], color: '#6B7280' },
    ].filter(item => item.value > 0);
  };

  // ================= VISITOR TREND =================
  const getVisitorTrend = () => {
    const dailyVisits = visitorStats?.dailyVisits || {};
    const days = 7;
    const data = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayKey = date.toISOString().split('T')[0];
      const dateStr = date.toLocaleDateString('en-PK', { day: 'numeric', month: 'short' });

      data.push({
        date: dateStr,
        visitors: dailyVisits[dayKey] || 0,
      });
    }

    return data;
  };

  const revenueData = getRevenueTrend();
  const topProducts = getTopProducts();
  const statusData = getOrderStatusData();
  const visitorData = getVisitorTrend();

  // ================= STAT CARDS =================
  const statCards = [
    {
      label: 'Total Revenue',
      value: `Rs. ${totalRevenue.toLocaleString()}`,
      icon: DollarSign,
      color: '#F5AEC0',
      trend: '+12%',
      trendUp: true,
    },
    {
      label: 'Total Orders',
      value: totalOrders,
      icon: ShoppingBag,
      color: '#DC829A',
      trend: `${processingOrders} pending`,
      trendUp: true,
    },
    {
      label: 'Total Visitors',
      value: visitorStats?.totalVisitors || 0,
      icon: Users,
      color: '#9B3F5B',
      trend: `${visitorStats?.liveVisitors || 0} live`,
      trendUp: true,
    },
    {
      label: 'Avg Order Value',
      value: `Rs. ${avgOrderValue.toLocaleString()}`,
      icon: TrendingUp,
      color: '#EF4444',
      trend: `${completedOrders} completed`,
      trendUp: true,
    },
  ];

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="text-pink-500" size={28} /> Analytics Dashboard
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Real-time sales, visitors, aur product insights
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: '#FFF2F6' }}>
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#10B981' }}></div>
          <span className="text-xs font-bold" style={{ color: '#9B3F5B' }}>
            Live: {visitorStats?.liveVisitors || 0} visitors
          </span>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white rounded-xl shadow-md p-5 card-lift">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}>
                  <Icon size={24} style={{ color: stat.color }} />
                </div>
                <span className="text-[10px] font-bold px-2 py-1 rounded-full"
                  style={{ backgroundColor: '#FFF2F6', color: '#9B3F5B' }}>
                  {stat.trend}
                </span>
              </div>
              <p className="text-xs font-semibold text-gray-500 mb-1">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* REVENUE TREND + VISITOR TREND */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Revenue Trend */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <TrendingUp size={20} className="text-pink-500" /> Revenue Trend (7 Days)
            </h2>
          </div>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F5AEC0" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#F5AEC0" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #F5AEC0',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value) => [`Rs. ${value}`, 'Revenue']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#DC829A"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Visitor Trend */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Eye size={20} className="text-pink-500" /> Visitors (7 Days)
            </h2>
          </div>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={visitorData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #F5AEC0',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value) => [value, 'Visitors']}
                />
                <Bar dataKey="visitors" fill="#DC829A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {/* TOP PRODUCTS + ORDER STATUS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Top Products */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <Star size={20} className="text-pink-500" /> Top Selling Products
          </h2>

          {topProducts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No sales yet.</p>
          ) : (
            <div className="space-y-3">
              {topProducts.map((product, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg"
                  style={{ backgroundColor: '#FFF2F6' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm shrink-0"
                    style={{ backgroundColor: 'var(--color-button-bg)' }}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: '#9B3F5B' }}>
                      {product.title}
                    </p>
                    <p className="text-xs" style={{ color: '#DC829A' }}>
                      {product.quantity} sold
                    </p>
                  </div>
                  <p className="font-bold text-sm shrink-0" style={{ color: 'var(--color-link)' }}>
                    Rs. {product.revenue.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order Status Pie */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
            <Package size={20} className="text-pink-500" /> Order Status
          </h2>

          {statusData.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No orders yet.</p>
          ) : (
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={50}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #F5AEC0',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

      </div>

      {/* TRAFFIC DETAILS */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
          <Eye size={20} className="text-pink-500" /> Website Traffic
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 rounded-xl" style={{ backgroundColor: '#FFF2F6' }}>
            <p className="text-3xl font-bold" style={{ color: 'var(--color-link)' }}>
              {visitorStats?.totalVisitors || 0}
            </p>
            <p className="text-xs font-semibold mt-1" style={{ color: '#9B3F5B' }}>
              Total Visitors
            </p>
          </div>
          <div className="text-center p-4 rounded-xl" style={{ backgroundColor: '#FFF2F6' }}>
            <p className="text-3xl font-bold" style={{ color: 'var(--color-link)' }}>
              {visitorStats?.todayVisitors || 0}
            </p>
            <p className="text-xs font-semibold mt-1" style={{ color: '#9B3F5B' }}>
              Today
            </p>
          </div>
          <div className="text-center p-4 rounded-xl" style={{ backgroundColor: '#FFF2F6' }}>
            <p className="text-3xl font-bold" style={{ color: 'var(--color-link)' }}>
              {visitorStats?.liveVisitors || 0}
            </p>
            <p className="text-xs font-semibold mt-1" style={{ color: '#9B3F5B' }}>
              Live Now
            </p>
          </div>
          <div className="text-center p-4 rounded-xl" style={{ backgroundColor: '#FFF2F6' }}>
            <p className="text-3xl font-bold" style={{ color: 'var(--color-link)' }}>
              {visitorStats?.totalPageViews || 0}
            </p>
            <p className="text-xs font-semibold mt-1" style={{ color: '#9B3F5B' }}>
              Page Views
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Analytics;